import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api } from '../services/api';
import Button from '../components/UI/Button';
import Input from '../components/UI/Input';
import { Check, ChevronLeft, ChevronRight, Upload, Briefcase, ShoppingCart, TrendingUp, UserPlus, Info, Star, MessageCircle, FileText, BarChart, Video, Megaphone, Zap, Plus, Trash2, Award, Coins, Heart, ShieldCheck } from 'lucide-react';
import { PricingConfig, ListingStatus } from '../types';
import { TRL_LEVELS } from '../constants';

// Helper for Icons
const IconMap: any = {
  Star: Star,
  MessageCircle: MessageCircle,
  FileText: FileText,
  BarChart: BarChart,
  Video: Video,
  Megaphone: Megaphone,
  Zap: Zap
};

const STEPS = [
  { id: 1, title: 'نوع آگهی' },
  { id: 2, title: 'شاخص‌های کلیدی' },
  { id: 3, title: 'جزئیات تکمیلی' },
  { id: 4, title: 'پرداخت' },
];

const CreateListingPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [docUploading, setDocUploading] = useState<null | 'valuation' | 'turnover' | 'legal' | 'technical'>(null);
  const [brokerageTermsContent, setBrokerageTermsContent] = useState<string>('');
  const [categories, setCategories] = useState<any[]>([]);
  
  // Listing types settings
  const [enabledTypes, setEnabledTypes] = useState({
    philanthropic: true,
    micro_investment: true
  });
  
  // Pricing state with defaults
  const [pricing, setPricing] = useState<PricingConfig>({
      baseFee: 300000,
      services: []
  });

  useEffect(() => {
    const fetchPricing = async () => {
        try {
            const config = await api.getPricing();
            if (config) setPricing(config);
        } catch (error) {
            console.error("Error fetching pricing:", error);
        }
    };
    
    const fetchListingTypesSettings = async () => {
        try {
            const response = await fetch(`${import.meta.env.VITE_API_URL || 'https://tbiz.ir'}/backend/api.php?path=listing-types-settings`);
            const data = await response.json();
            if (data.success) {
                setEnabledTypes(data.settings);
            }
        } catch (error) {
            console.error("Error fetching listing types settings:", error);
        }
    };
    
    const fetchBrokerageTerms = async () => {
        try {
            const response = await fetch(`${import.meta.env.VITE_API_URL || 'https://tbiz.ir'}/backend/api.php?path=brokerage-terms`);
            const data = await response.json();
            if (data.success && data.terms) {
                setBrokerageTermsContent(data.terms);
            }
        } catch (error) {
            console.error("Error fetching brokerage terms:", error);
        }
    };
    
    const fetchCategories = async () => {
        try {
            const response = await fetch(`${import.meta.env.VITE_API_URL || 'https://tbiz.ir'}/backend/categories_simple.php`);
            const data = await response.json();
            console.log('Categories fetched:', data);
            if (data.success) {
                setCategories(data.categories || []);
                console.log('Categories set:', data.categories);
            }
        } catch (error) {
            console.error("Error fetching categories:", error);
        }
    };
    
    fetchPricing();
    fetchListingTypesSettings();
    fetchBrokerageTerms();
    fetchCategories();
    
    if (id) {
        const fetchListing = async () => {
            const listing = await api.getListingById(id);
            if (listing) {
                setFormData({
                    title: listing.title,
                    type: listing.type,
                    category: listing.category,
                    price: listing.price.toLocaleString(),
                    description: listing.description,
                    location: listing.location,
                    imageUrls: listing.imageUrls || [],
                    videoUrls: listing.videoUrls || [],
                    trl: listing.trl || 5,
                    team: listing.teamMembers || [],
                    monthlyRevenue: (listing.monthlyRevenue || 0).toLocaleString(),
                    monthlyProfit: (listing.monthlyProfit || 0).toLocaleString(),
                    foundedYear: listing.foundedYear || '',
                    employeeCount: (listing.employeeCount || 0).toString(),
                    equityOffered: (listing.equityOffered || 0).toString(),
                    roiDuration: (listing.roiDuration || 0).toString(),
                    purchaseTimeline: listing.purchaseTimeline || 'immediately',
                    assetType: listing.assetType || 'brand',
                    registrationNumber: listing.registrationNumber || '',
                    valuationMethod: listing.valuationMethod || '',
                    usageHistory: listing.usageHistory || '',
                    brandTerritory: listing.brandTerritory || '',
                    brandClass: listing.brandClass || '',
                    domainName: listing.domainName || '',
                    socialMediaHandles: listing.socialMediaHandles || '',
                    techStack: listing.techStack || '',
                    licenseType: listing.licenseType || '',
                    activeUsers: listing.activeUsers || '',
                    revenueModel: listing.revenueModel || '',
                    platform: listing.platform || '',
                    database: listing.database || '',
                    patentStatus: listing.patentStatus || 'granted',
                    expirationDate: listing.expirationDate || '',
                    jurisdiction: listing.jurisdiction || '',
                    priorityDate: listing.priorityDate || '',
                    filingDate: listing.filingDate || '',
                    hasValuationFile: listing.hasValuationFile || false,
                    hasFinancialTurnoverFile: listing.hasFinancialTurnoverFile || false,
                    hasVerification3: listing.hasVerification3 || false,
                    hasVerification4: listing.hasVerification4 || false,
                    valuationFileUrl: listing.valuationFileUrl || '',
                    financialTurnoverFileUrl: listing.financialTurnoverFileUrl || '',
                    legalVerificationFileUrl: listing.legalVerificationFileUrl || '',
                    technicalVerificationFileUrl: listing.technicalVerificationFileUrl || '',
                    verificationDocsApproved: listing.verificationDocsApproved || false,
                    projectGoal: listing.projectGoal || '',
                    socialImpact: listing.socialImpact || '',
                    reportingMethod: listing.reportingMethod || '',
                    beneficiaries: listing.beneficiaries || '',
                    pointRatio: (listing.pointRatio || 1).toString(),
                    recognitionLevels: (listing.recognitionLevels || []).map(rl => ({
                        minUnits: rl.minUnits.toString(),
                        label: rl.label,
                        description: rl.description
                    })),
                    minInvestmentAmount: (listing.minInvestmentAmount || 0).toLocaleString(),
                    totalTargetAmount: (listing.totalTargetAmount || 0).toLocaleString(),
                    currentInvestorsCount: (listing.currentInvestorsCount || 0).toString(),
                    profitSharingModel: listing.profitSharingModel || '',
                    unitPrice: (listing.unitPrice || 0).toLocaleString(),
                    totalUnits: (listing.totalUnits || 0).toString(),
                    brokerageType: listing.brokerageType || 'direct',
                    brokeragePercentage: (listing.brokeragePercentage || 0).toString(),
                    selectedServices: []
                });
            }
        };
        fetchListing();
    }
  }, [id]);
  
  const [formData, setFormData] = useState({
    title: '',
    type: 'sale',
    category: '',
    price: '',
    description: '',
    location: '',
    imageUrls: [] as string[],
    videoUrls: [] as string[],
    
    // Tech Metrics
    trl: 5,
    
    // Team
    team: [] as { name: string; role: string }[],
    
    // Business Metrics
    monthlyRevenue: '',
    monthlyProfit: '',
    foundedYear: '',
    employeeCount: '',
    equityOffered: '',
    roiDuration: '',
    purchaseTimeline: 'immediately',

    // Intangible Assets
    assetType: 'brand',
    registrationNumber: '',
    valuationMethod: '',
    usageHistory: '',
    brandTerritory: '',
    brandClass: '',
    domainName: '',
    socialMediaHandles: '',
    techStack: '',
    licenseType: '',
    activeUsers: '',
    revenueModel: '',
    platform: '',
    database: '',
    patentStatus: 'granted',
    expirationDate: '',
    jurisdiction: '',
    priorityDate: '',
    filingDate: '',

    // Verification Files
    hasValuationFile: false,
    hasFinancialTurnoverFile: false,
    hasVerification3: false,
    hasVerification4: false,
    valuationFileUrl: '',
    financialTurnoverFileUrl: '',
    legalVerificationFileUrl: '',
    technicalVerificationFileUrl: '',
    verificationDocsApproved: false,

    // Philanthropic
    projectGoal: '',
    socialImpact: '',
    reportingMethod: '',
    beneficiaries: '',
    pointRatio: '1', // Default 1 point per unit
    recognitionLevels: [] as { minUnits: string; label: string; description: string }[],

    // Micro-investment
    minInvestmentAmount: '',
    totalTargetAmount: '',
    currentInvestorsCount: '',
    profitSharingModel: '',
    unitPrice: '',
    totalUnits: '',

    // Brokerage
    brokerageType: 'direct',
    brokeragePercentage: '',
    acceptedBrokerageTerms: false,

    // Selected Services IDs
    selectedServices: [] as string[]
  });

  const [discountInput, setDiscountInput] = useState('');
  const [appliedDiscount, setAppliedDiscount] = useState<any>(null);
  const [discountError, setDiscountError] = useState('');
  const isInvestorType = formData.type === 'investor';

  const availableServices = useMemo(() => {
    return (pricing.services || []).filter(service => {
      const allowedTypes = Array.isArray(service.applicableTypes) ? service.applicableTypes : [];
      const brokerageScope = service.brokerageScope || 'all';
      const typeAllowed = allowedTypes.length === 0 || allowedTypes.includes(formData.type as any);
      const brokerageAllowed = brokerageScope === 'all' || brokerageScope === formData.brokerageType;
      return typeAllowed && brokerageAllowed;
    });
  }, [pricing.services, formData.type, formData.brokerageType]);

  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      selectedServices: prev.selectedServices.filter(id => availableServices.some(s => s.id === id))
    }));
  }, [availableServices]);

  const updateForm = (key: string, value: any) => {
    if (key === 'type' || key === 'brokerageType') {
      setAppliedDiscount(null);
      setDiscountError('');
    }
    setFormData(prev => {
        const nextState = { ...prev, [key]: value };
        if (key === 'type' && value === 'investor') {
            nextState.brokerageType = 'direct';
        }
        
        // Algorithm: unitPrice * totalUnits = price
        if (key === 'unitPrice' || key === 'totalUnits') {
            const unitPrice = parseInt(nextState.unitPrice.replace(/,/g, ''), 10) || 0;
            const totalUnits = parseInt(nextState.totalUnits, 10) || 0;
            nextState.price = (unitPrice * totalUnits).toLocaleString('en-US');
        }
        
        return nextState;
    });
  };

  const handleCurrencyChange = (key: string, value: string) => {
    // 1. Convert Farsi digits to English
    const enValue = value.replace(/[۰-۹]/g, d => '۰۱۲۳۴۵۶۷۸۹'.indexOf(d).toString());
    // 2. Remove non-digits
    const raw = enValue.replace(/[^0-9]/g, '');
    
    if (!raw) {
        updateForm(key, '');
        return;
    }
    // 3. Format with commas
    const formatted = Number(raw).toLocaleString('en-US');
    updateForm(key, formatted);
  };

  const toggleService = (serviceId: string) => {
    setFormData(prev => {
      const isSelected = prev.selectedServices.includes(serviceId);
      if (isSelected) {
        return { ...prev, selectedServices: prev.selectedServices.filter(id => id !== serviceId) };
      } else {
        return { ...prev, selectedServices: [...prev.selectedServices, serviceId] };
      }
    });
  };

  const handleNext = () => {
    // Basic validation
    if (currentStep === 1) {
        if (!formData.type) {
            alert('لطفا نوع آگهی را انتخاب کنید.');
            return;
        }
    } else if (currentStep === 2) {
        if (formData.type === 'sale') {
            if (!formData.monthlyRevenue || !formData.monthlyProfit) {
                alert('لطفا شاخص‌های مالی را وارد کنید.');
                return;
            }
        } else if (formData.type === 'investment') {
            if (!formData.price || !formData.equityOffered) {
                alert('لطفا مبلغ سرمایه و درصد سهام را وارد کنید.');
                return;
            }
        } else if (formData.type === 'intangible') {
            if (!formData.registrationNumber) {
                alert('لطفا شماره ثبت را وارد کنید.');
                return;
            }
        } else if (formData.type === 'micro_investment') {
            if (!formData.minInvestmentAmount || !formData.totalTargetAmount) {
                alert('لطفا مبالغ سرمایه‌گذاری را وارد کنید.');
                return;
            }
        } else if (formData.type === 'philanthropic') {
            if (!formData.projectGoal || !formData.socialImpact) {
                alert('لطفا هدف پروژه و شاخص تاثیر را وارد کنید.');
                return;
            }
        }
    } else if (currentStep === 3) {
        if (!formData.title || !formData.category || !formData.location || !formData.description) {
            alert('لطفا تمامی فیلدهای اجباری را پر کنید.');
            return;
        }
    }

    if (currentStep < 4) setCurrentStep(c => c + 1);
  };

  const handlePrev = () => {
    if (currentStep > 1) setCurrentStep(c => c - 1);
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
       setLoading(true);
       try {
           const files = Array.from(e.target.files);
           const urls = await api.uploadFiles(files);
           updateForm('imageUrls', [...formData.imageUrls, ...urls]);
       } catch (error) {
           console.error("Upload failed", error);
           alert("خطا در آپلود فایل‌ها");
       } finally {
           setLoading(false);
       }
    }
  };

  const handleVideoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
       const files = Array.from(e.target.files);
       if (formData.videoUrls.length + files.length > 2) {
           alert("حداکثر ۲ ویدیو می‌توانید آپلود کنید.");
           return;
       }
       setLoading(true);
       try {
           const urls = await api.uploadFiles(files);
           updateForm('videoUrls', [...formData.videoUrls, ...urls]);
       } catch (error) {
           console.error("Upload failed", error);
           alert("خطا در آپلود ویدیو");
       } finally {
           setLoading(false);
       }
    }
  };

  const handleVerificationUpload = async (
    e: React.ChangeEvent<HTMLInputElement>,
    kind: 'valuation' | 'turnover' | 'legal' | 'technical'
  ) => {
    if (!e.target.files || e.target.files.length === 0) return;
    const file = e.target.files[0];

    const map = {
      valuation: { flag: 'hasValuationFile', url: 'valuationFileUrl' },
      turnover: { flag: 'hasFinancialTurnoverFile', url: 'financialTurnoverFileUrl' },
      legal: { flag: 'hasVerification3', url: 'legalVerificationFileUrl' },
      technical: { flag: 'hasVerification4', url: 'technicalVerificationFileUrl' },
    } as const;

    setDocUploading(kind);
    try {
      const urls = await api.uploadFiles([file]);
      if (urls.length > 0) {
        // Admin controls the final verification badges after review.
        updateForm(map[kind].flag, false);
        updateForm(map[kind].url, urls[0]);
        updateForm('verificationDocsApproved', false);
      }
    } catch (error) {
      console.error('Verification upload failed', error);
      alert('خطا در آپلود مدرک. دوباره تلاش کنید.');
    } finally {
      setDocUploading(null);
      e.target.value = '';
    }
  };

  const handleApplyDiscount = async () => {
    setDiscountError('');
    if (!discountInput) return;
    const discount = await api.validateDiscountCode(discountInput, {
      listingType: formData.type,
      brokerageType: formData.brokerageType
    });
    if (discount) {
        setAppliedDiscount(discount);
    } else {
        setDiscountError('کد تخفیف نامعتبر است یا منقضی شده.');
        setAppliedDiscount(null);
    }
  };

  const calculateTotal = () => {
    let base = isInvestorType ? 0 : (formData.brokerageType === 'brokerage' ? (pricing.brokerageFee || pricing.baseFee) : pricing.baseFee);
    let discountAmount = 0;
    
    if (appliedDiscount) {
        discountAmount = (base * appliedDiscount.discountPercent) / 100;
        if (appliedDiscount.maxDiscountAmount && discountAmount > appliedDiscount.maxDiscountAmount) {
            discountAmount = appliedDiscount.maxDiscountAmount;
        }
    }
    
    let total = base - discountAmount;
    
    formData.selectedServices.forEach(serviceId => {
       const service = availableServices.find(s => s.id === serviceId);
       if (service) total += service.price;
    });
    return Math.max(0, total);
  };

  const handlePayment = async () => {
    // Check if user is authenticated
    if (!user) {
      alert('جلسه شما منقضی شده است. لطفاً دوباره وارد شوید.');
      navigate('/login');
      return;
    }
    
    // Check if we have an auth token
    const token = localStorage.getItem('tbiz_auth_token');
    if (!token) {
      alert('جلسه شما منقضی شده است. لطفاً دوباره وارد شوید.');
      navigate('/login');
      return;
    }
    
    // Validate brokerage terms acceptance
    if (formData.brokerageType === 'brokerage' && !formData.acceptedBrokerageTerms) {
      alert('لطفا شرایط کارگزاری را مطالعه کرده و تیک تایید را بزنید.');
      return;
    }
    
    setLoading(true);
    try {
      // Helper to parse currency strings back to numbers
      const parseCurrency = (str: string) => {
          if (!str) return 0;
          return parseInt(str.replace(/,/g, '').replace(/[^0-9]/g, ''), 10) || 0;
      };

      const listingData = {
        ownerId: user?.id || 'guest',
        title: formData.title,
        description: formData.description,
        price: parseCurrency(formData.price),
        location: formData.location,
        category: formData.category,
        type: formData.type as any,
        imageUrls: formData.imageUrls,
        videoUrls: formData.videoUrls,
        trl: formData.trl,
        teamMembers: formData.team,
        monthlyRevenue: parseCurrency(formData.monthlyRevenue),
        monthlyProfit: parseCurrency(formData.monthlyProfit),
        foundedYear: formData.foundedYear,
        employeeCount: parseInt(formData.employeeCount) || 0,
        equityOffered: parseInt(formData.equityOffered) || 0,
        roiDuration: parseInt(formData.roiDuration) || 0,
        purchaseTimeline: formData.purchaseTimeline,
        
        // New Fields
        assetType: formData.assetType as 'brand' | 'software' | 'patent' | 'trademark' | 'other',
        registrationNumber: formData.registrationNumber,
        valuationMethod: formData.valuationMethod,
        usageHistory: formData.usageHistory,
        brandTerritory: formData.brandTerritory,
        brandClass: formData.brandClass,
        domainName: formData.domainName,
        socialMediaHandles: formData.socialMediaHandles,
        techStack: formData.techStack,
        licenseType: formData.licenseType,
        activeUsers: formData.activeUsers,
        revenueModel: formData.revenueModel,
        platform: formData.platform,
        database: formData.database,
        patentStatus: formData.patentStatus as 'pending' | 'granted',
        expirationDate: formData.expirationDate,
        jurisdiction: formData.jurisdiction,
        priorityDate: formData.priorityDate,
        filingDate: formData.filingDate,
      hasValuationFile: Boolean(formData.hasValuationFile),
      hasFinancialTurnoverFile: Boolean(formData.hasFinancialTurnoverFile),
      hasVerification3: Boolean(formData.hasVerification3),
      hasVerification4: Boolean(formData.hasVerification4),
      verificationDocsApproved: Boolean(formData.verificationDocsApproved),
        valuationFileUrl: formData.valuationFileUrl,
        financialTurnoverFileUrl: formData.financialTurnoverFileUrl,
        legalVerificationFileUrl: formData.legalVerificationFileUrl,
        technicalVerificationFileUrl: formData.technicalVerificationFileUrl,
        minInvestmentAmount: parseCurrency(formData.minInvestmentAmount),
        totalTargetAmount: parseCurrency(formData.totalTargetAmount),
        currentInvestorsCount: parseInt(formData.currentInvestorsCount) || 0,
        profitSharingModel: formData.profitSharingModel,
        unitPrice: parseCurrency(formData.unitPrice),
        totalUnits: parseInt(formData.totalUnits) || 0,
        projectGoal: formData.projectGoal,
        socialImpact: formData.socialImpact,
        reportingMethod: formData.reportingMethod,
        beneficiaries: formData.beneficiaries,
        pointRatio: parseFloat(formData.pointRatio) || 1,
        recognitionLevels: formData.recognitionLevels.map(rl => ({
          minUnits: parseInt(rl.minUnits) || 0,
          label: rl.label,
          description: rl.description
        })),
        brokerageType: formData.brokerageType as any,
        brokeragePercentage: parseInt(formData.brokeragePercentage) || 0,
        ownerPhone: user?.phone
      };
      
      let res;
      if (id) {
          const updatedListingData = { ...listingData, status: 'pending' as ListingStatus };
          res = await api.updateListing(id, updatedListingData);
          if (res) {
              api.trackEvent('listing_update', res.id, res.title, { type: res.type });
              navigate('/dashboard');
          }
      } else {
          res = await api.createListing(listingData);
          api.trackEvent('listing_create', res.id, res.title, { type: res.type });
          
          // Request Payment Token and Redirect to REAL Gateway
          try {
              const baseFeeRaw = isInvestorType ? 0 : (formData.brokerageType === 'brokerage' ? (pricing.brokerageFee || pricing.baseFee) : pricing.baseFee);
              const baseFee = Number(baseFeeRaw) || 0;
              const selectedItems = formData.selectedServices.map(id => {
                  const s = availableServices.find(svc => svc.id === id);
                  return s ? { id: s.id, title: s.title, price: Number(s.price) || 0 } : null;
              }).filter(Boolean);
              const totalAmount = Number(calculateTotal()) || 0;
              if (totalAmount <= 0) {
                  setLoading(false);
                  navigate('/dashboard', { state: { newListingCreated: true } });
                  return;
              }

              const metadata = {
                  items: [
                      ...(baseFee > 0 ? [{ id: 'base_fee', title: `ثبت آگهی ${formData.brokerageType === 'brokerage' ? 'کارگزاری' : 'مستقیم'}`, price: baseFee }] : []),
                      ...selectedItems
                  ],
                  discount: appliedDiscount ? {
                      code: discountInput,
                      amount: (baseFee * appliedDiscount.discountPercent) / 100 > (appliedDiscount.maxDiscountAmount || Infinity) 
                          ? appliedDiscount.maxDiscountAmount 
                          : (baseFee * appliedDiscount.discountPercent) / 100
                  } : null
              };

              const paymentRes = await api.createTransaction({
                  userId: user?.id,
                  amount: totalAmount,
                  listingId: res.id,
                  listingTitle: res.title,
                  listingType: formData.type,
                  brokerageType: formData.brokerageType,
                  type: 'listing_fee',
                  metadata: metadata
              });
              
              if (paymentRes && (paymentRes as any).paymentUrl) {
                  window.location.href = (paymentRes as any).paymentUrl;
              } else {
                  setLoading(false);
                  navigate('/dashboard', { state: { newListingCreated: true } });
              }
          } catch (error) {
              console.error("Payment failed", error);
              setLoading(false);
              alert(error instanceof Error ? error.message : "خطا در شروع پرداخت");
              navigate('/dashboard');
          }
      }
    } catch (e) {
      console.error(e);
      setLoading(false);
      
      // Handle authentication errors specifically
      if (e instanceof Error) {
        if (e.message.includes('Unauthorized') || e.message.includes('401')) {
          // Clear invalid auth data
          localStorage.removeItem('tbiz_auth_token');
          localStorage.removeItem('tbiz_current_user');
          alert('جلسه شما منقضی شده است. لطفاً دوباره وارد شوید.');
          navigate('/login');
          return;
        }
        
        // Handle other specific errors
        if (e.message.includes('403') || e.message.includes('Forbidden')) {
          alert('شما مجوز انجام این عمل را ندارید.');
          return;
        }
        
        if (e.message.includes('400') || e.message.includes('Bad Request')) {
          alert('اطلاعات ارسالی نامعتبر است. لطفاً فرم را بررسی کنید.');
          return;
        }
        
        // Generic error message
        alert(`خطا در ثبت آگهی: ${e.message}`);
      } else {
        alert('خطا در ثبت آگهی. دوباره تلاش کنید.');
      }
    }
  };

  const addRecognitionLevel = () => {
    updateForm('recognitionLevels', [...formData.recognitionLevels, { minUnits: '', label: '', description: '' }]);
  };

  const updateRecognitionLevel = (index: number, key: string, value: string) => {
    const updated = [...formData.recognitionLevels];
    updated[index] = { ...updated[index], [key]: value };
    updateForm('recognitionLevels', updated);
  };

  const removeRecognitionLevel = (index: number) => {
    const updated = [...formData.recognitionLevels];
    updated.splice(index, 1);
    updateForm('recognitionLevels', updated);
  };

  const formatPrice = (price: number) => price.toLocaleString();

  // --- Render Functions ---

  const renderSaleMetrics = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 animate-fadeIn">
       <div className="col-span-full bg-blue-50 p-4 rounded-xl border border-blue-100 flex gap-3 text-sm text-blue-800">
          <Info className="shrink-0" size={20}/>
          <p>خریداران کسب‌وکار به اعداد اعتماد می‌کنند. لطفاً میانگین درآمد ماهانه و سود خالص ۶ ماه گذشته را وارد کنید.</p>
       </div>
       <Input 
         label="میانگین درآمد ماهانه (تومان) *"
         type="text"
         value={formData.monthlyRevenue}
         onChange={e => handleCurrencyChange('monthlyRevenue', e.target.value)}
         placeholder="مثلا: ۱۰۰,۰۰۰,۰۰۰"
         dir="ltr"
       />
       <Input 
         label="میانگین سود خالص ماهانه (تومان) *"
         type="text"
         value={formData.monthlyProfit}
         onChange={e => handleCurrencyChange('monthlyProfit', e.target.value)}
         placeholder="مثلا: ۵۰,۰۰۰,۰۰۰"
         dir="ltr"
       />
       <Input 
         label="سال تاسیس"
         placeholder="مثلا: ۱۳۹۸"
         type="number"
         value={formData.foundedYear}
         onChange={e => updateForm('foundedYear', e.target.value)}
       />
       <Input 
         label="تعداد پرسنل فعال"
         type="number"
         value={formData.employeeCount}
         onChange={e => updateForm('employeeCount', e.target.value)}
       />
       
       <div className="col-span-full mt-4">
          <h4 className="font-bold text-gray-700 mb-2 text-sm">شاخص بلوغ فناوری</h4>
          <div className="bg-gray-50 p-3 rounded-xl border border-gray-200">
             <div className="flex justify-between mb-1 text-xs font-bold text-gray-500">
                <span>سطح فناوری (TRL)</span>
                <span className="text-teal-600">{formData.trl}/9</span>
             </div>
             <input type="range" min="1" max="9" value={formData.trl} onChange={e => updateForm('trl', parseInt(e.target.value))} className="w-full h-1.5 bg-gray-300 rounded-lg appearance-none cursor-pointer accent-teal-600"/>
             <p className="text-[10px] text-gray-500 mt-2">{TRL_LEVELS.find(l => l.id === formData.trl)?.label}</p>
          </div>
       </div>
    </div>
  );

  const renderInvestmentMetrics = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 animate-fadeIn">
       <div className="col-span-full bg-purple-50 p-4 rounded-xl border border-purple-100 flex gap-3 text-sm text-purple-800">
          <TrendingUp className="shrink-0" size={20}/>
          <p>سرمایه‌گذاران به دنبال شفافیت در سهام و بازگشت سرمایه هستند. پیشنهاد جذاب ارائه دهید.</p>
       </div>
       <div className="col-span-full">
         <Input 
            label="میزان سرمایه درخواستی (تومان) *"
           type="text"
           value={formData.price}
           onChange={e => handleCurrencyChange('price', e.target.value)}
           placeholder="کل مبلغ مورد نیاز برای این راند جذب سرمایه"
           dir="ltr"
         />
       </div>
       <Input 
          label="درصد سهام پیشنهادی (%) *"
         type="number"
         max={100}
         value={formData.equityOffered}
         onChange={e => updateForm('equityOffered', e.target.value)}
       />
       <Input 
         label="پیش‌بینی بازگشت سرمایه (ماه)"
         type="number"
         placeholder="مثلا: ۲۴ ماه"
         value={formData.roiDuration}
         onChange={e => updateForm('roiDuration', e.target.value)}
       />
       
       <div className="col-span-full">
          <h4 className="font-bold text-gray-700 mb-2 text-sm">شاخص‌های بلوغ (برای استارتاپ‌ها)</h4>
          <div className="grid grid-cols-2 gap-4">
             <div className="bg-gray-50 p-3 rounded-xl border border-gray-200">
                <div className="flex justify-between mb-1 text-xs font-bold text-gray-500">
                   <span>فناوری (TRL)</span>
                   <span className="text-teal-600">{formData.trl}/9 - {TRL_LEVELS.find(l => l.id === formData.trl)?.label}</span>
                </div>
                <input type="range" min="1" max="9" value={formData.trl} onChange={e => updateForm('trl', parseInt(e.target.value))} className="w-full h-1.5 bg-gray-300 rounded-lg appearance-none cursor-pointer accent-teal-600"/>
                <p className="text-[10px] text-gray-500 mt-2">{TRL_LEVELS.find(l => l.id === formData.trl)?.label}</p>
             </div>
          </div>
       </div>
    </div>
  );

  const renderDemandMetrics = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 animate-fadeIn">
       <div className="col-span-full bg-teal-50 p-4 rounded-xl border border-teal-100 flex gap-3 text-sm text-teal-800">
          <ShoppingCart className="shrink-0" size={20}/>
          <p>فروشندگان مایلند بدانند شما چقدر در خرید جدی هستید. بودجه و زمان خود را مشخص کنید.</p>
       </div>
       <div className="col-span-full">
          <Input 
            label="حداکثر بودجه خرید (تومان)"
            type="text"
            value={formData.price}
            onChange={e => handleCurrencyChange('price', e.target.value)}
            dir="ltr"
          />
       </div>
       <div className="col-span-full">
          <label className="block text-sm font-medium text-gray-700 mb-1.5">زمان‌بندی خرید</label>
          <select 
             className="w-full rounded-xl border-gray-300 shadow-sm focus:border-teal-500 focus:ring-teal-500 py-2.5 px-3 border"
             value={formData.purchaseTimeline}
             onChange={e => updateForm('purchaseTimeline', e.target.value)}
          >
             <option value="immediately">فوری (آماده معامله)</option>
             <option value="1_month">تا ۱ ماه آینده</option>
             <option value="3_months">تا ۳ ماه آینده</option>
             <option value="research">در حال بررسی اولیه</option>
          </select>
       </div>
    </div>
  );

  const renderInvestorMetrics = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 animate-fadeIn">
       <div className="col-span-full bg-orange-50 p-4 rounded-xl border border-orange-100 flex gap-3 text-sm text-orange-800">
          <UserPlus className="shrink-0" size={20}/>
          <p>کارآفرینان به دنبال "پول هوشمند" هستند. علاوه بر سرمایه، ارزش افزوده‌های خود را بیان کنید.</p>
       </div>
       <div className="col-span-full">
          <Input 
             label="سقف توان سرمایه‌گذاری (تومان)"
             type="text"
             value={formData.price}
             onChange={e => handleCurrencyChange('price', e.target.value)}
             dir="ltr"
          />
       </div>
       <Input 
         label="تعداد سرمایه‌گذاری‌های موفق قبلی"
         type="number"
         placeholder="مثلا: ۵ استارتاپ"
       />
       <div className="col-span-full">
         <label className="flex items-center gap-2 p-3 border rounded-xl bg-gray-50 cursor-pointer">
            <input type="checkbox" className="w-5 h-5 rounded text-teal-600 focus:ring-teal-500" />
            <span className="text-sm font-medium text-gray-700">امکان ارائه منتورینگ و مشاوره تخصصی دارم</span>
         </label>
       </div>
    </div>
  );

  const renderIntangibleMetrics = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 animate-fadeIn">
       {formData.type === 'intangible' && (
         <div className="col-span-full bg-indigo-50 p-4 rounded-xl border border-indigo-100 flex gap-3 text-sm text-indigo-800">
           <Award className="shrink-0" size={20}/>
           <p>آگهی‌های نامشهود ارزش استراتژیک دارند. جزئیات ثبتی و روش ارزش‌گذاری را دقیق وارد کنید.</p>
         </div>
       )}
       <div className="col-span-full">
          <label className="block text-sm font-medium text-gray-700 mb-1.5">نوع آگهی</label>
          <select 
             className="w-full rounded-xl border-gray-300 shadow-sm focus:border-teal-500 focus:ring-teal-500 py-2.5 px-3 border"
             value={formData.assetType}
             onChange={e => updateForm('assetType', e.target.value)}
          >
             <option value="brand">برند و علامت تجاری</option>
             <option value="software">نرم‌افزار و اپلیکیشن</option>
             <option value="patent">اختراع و پتنت</option>
             <option value="trademark">نام تجاری</option>
             <option value="other">سایر موارد</option>
          </select>
       </div>

       {/* Common Fields */}
       <Input 
          label="شماره ثبت / گواهی *"
         placeholder="مثلا: ۱۲۳۴۵۶"
         value={formData.registrationNumber}
         onChange={e => updateForm('registrationNumber', e.target.value)}
       />
       <Input 
          label="روش ارزش‌گذاری"
         placeholder="مثلا: روش هزینه / روش درآمد"
         value={formData.valuationMethod}
         onChange={e => updateForm('valuationMethod', e.target.value)}
       />

       {/* Specific Fields for Brand/Trademark */}
       {(formData.assetType === 'brand' || formData.assetType === 'trademark') && (
         <>
           <Input 
             label="قلمرو جغرافیایی ثبت"
             placeholder="مثلا: ایران / بین‌المللی"
             value={formData.brandTerritory}
             onChange={e => updateForm('brandTerritory', e.target.value)}
           />
           <Input 
             label="طبقه / کلاس برند"
             placeholder="مثلا: طبقه ۳۵ (تبلیغات)"
             value={formData.brandClass}
             onChange={e => updateForm('brandClass', e.target.value)}
           />
           <Input 
             label="نام دامنه مرتبط (اختیاری)"
             placeholder="مثلا: example.com"
             value={formData.domainName}
             onChange={e => updateForm('domainName', e.target.value)}
           />
         </>
       )}

       {/* Specific Fields for Software */}
       {formData.assetType === 'software' && (
         <>
           <Input 
             label="تکنولوژی‌های مورد استفاده"
             placeholder="مثلا: React, Node.js, Python"
             value={formData.techStack}
             onChange={e => updateForm('techStack', e.target.value)}
           />
           <Input 
             label="تعداد کاربران فعال"
             placeholder="مثلا: ۵۰۰۰ کاربر ماهانه"
             value={formData.activeUsers}
             onChange={e => updateForm('activeUsers', e.target.value)}
           />
           <Input 
             label="پلتفرم‌های پشتیبانی شده"
             placeholder="مثلا: Web, Android, iOS"
             value={formData.platform}
             onChange={e => updateForm('platform', e.target.value)}
           />
         </>
       )}

       {/* Specific Fields for Patent */}
       {formData.assetType === 'patent' && (
         <>
           <div className="col-span-1">
              <label className="block text-sm font-medium text-gray-700 mb-1.5">وضعیت پتنت</label>
              <select 
                 className="w-full rounded-xl border-gray-300 shadow-sm focus:border-teal-500 focus:ring-teal-500 py-2.5 px-3 border"
                 value={formData.patentStatus}
                 onChange={e => updateForm('patentStatus', e.target.value)}
              >
                 <option value="pending">در حال بررسی (Pending)</option>
                 <option value="granted">ثبت شده (Granted)</option>
              </select>
           </div>
           <Input 
             label="تاریخ انقضا / اعتبار"
             placeholder="مثلا: ۱۴۱۰/۰۵/۲۰"
             value={formData.expirationDate}
             onChange={e => updateForm('expirationDate', e.target.value)}
           />
           <Input 
             label="کشور / مرجع ثبت"
             placeholder="مثلا: ایران / USPTO"
             value={formData.jurisdiction}
             onChange={e => updateForm('jurisdiction', e.target.value)}
           />
           <Input 
             label="تاریخ اولویت (Priority Date)"
             placeholder="مثلا: ۱۳۹۹/۱۰/۱۵"
             value={formData.priorityDate}
             onChange={e => updateForm('priorityDate', e.target.value)}
           />
         </>
       )}

       <div className="col-span-full">
          <label className="block text-sm font-medium text-gray-700 mb-1.5">سابقه بهره‌برداری و جزئیات بیشتر</label>
          <textarea 
            className="w-full border border-gray-300 rounded-xl p-3 focus:ring-teal-500 focus:border-teal-500"
            rows={3}
            placeholder="توضیح دهید این آگهی تاکنون چگونه استفاده شده است و چه پتانسیل‌هایی دارد..."
            value={formData.usageHistory}
            onChange={e => updateForm('usageHistory', e.target.value)}
          ></textarea>
       </div>
    </div>
  );

  const renderMicroInvestmentMetrics = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 animate-fadeIn">
       <div className="col-span-full bg-emerald-50 p-4 rounded-xl border border-emerald-100 flex gap-3 text-sm text-emerald-800">
          <Coins className="shrink-0" size={20}/>
          <p>جذب سرمایه خرد (Crowdfunding) نیازمند اعتماد و شفافیت است. مبالغ و مدل سوددهی را مشخص کنید.</p>
       </div>
       <div className="col-span-full">
          <Input 
            label="کل مبلغ مورد نیاز (تومان) *"
            type="text"
            value={formData.price}
            onChange={e => handleCurrencyChange('price', e.target.value)}
            placeholder="مثلا: ۵۰۰,۰۰۰,۰۰۰"
            dir="ltr"
          />
          <Input 
            label="قیمت هر واحد/سهم (تومان)"
            type="text"
            value={formData.unitPrice}
            onChange={e => handleCurrencyChange('unitPrice', e.target.value)}
            placeholder="مثلا: ۱,۰۰۰,۰۰۰"
            dir="ltr"
          />
          <Input 
            label="تعداد کل واحدها"
            type="number"
            value={formData.totalUnits}
            onChange={e => updateForm('totalUnits', e.target.value)}
            placeholder="مثلا: ۵۰۰"
          />
       </div>
       <Input 
          label="حداقل مبلغ سرمایه‌گذاری (تومان) *"
         type="text"
         value={formData.minInvestmentAmount}
         onChange={e => handleCurrencyChange('minInvestmentAmount', e.target.value)}
         placeholder="مثلا: ۵,۰۰۰,۰۰۰"
         dir="ltr"
       />
       <Input 
         label="تعداد سرمایه‌گذاران فعلی"
         type="number"
         value={formData.currentInvestorsCount}
         onChange={e => updateForm('currentInvestorsCount', e.target.value)}
       />
       <Input 
         label="مدل تقسیم سود"
         placeholder="مثلا: مشارکت در سود سالانه"
         value={formData.profitSharingModel}
         onChange={e => updateForm('profitSharingModel', e.target.value)}
       />
       
       <div className="col-span-full mt-4">
          <h4 className="font-bold text-gray-700 mb-2 text-sm">شاخص بلوغ فناوری</h4>
          <div className="bg-gray-50 p-3 rounded-xl border border-gray-200">
             <div className="flex justify-between mb-1 text-xs font-bold text-gray-500">
                <span>سطح فناوری (TRL)</span>
                <span className="text-teal-600">{formData.trl}/9</span>
             </div>
             <input type="range" min="1" max="9" value={formData.trl} onChange={e => updateForm('trl', parseInt(e.target.value))} className="w-full h-1.5 bg-gray-300 rounded-lg appearance-none cursor-pointer accent-teal-600"/>
             <p className="text-[10px] text-gray-500 mt-2">{TRL_LEVELS.find(l => l.id === formData.trl)?.label}</p>
          </div>
       </div>
    </div>
  );

  const renderPhilanthropicMetrics = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 animate-fadeIn">
       <div className="col-span-full bg-rose-50 p-4 rounded-xl border border-rose-100 flex gap-3 text-sm text-rose-800">
          <Heart className="shrink-0" size={20}/>
          <p>کارهای عام‌المنفعه بر اساس تاثیر اجتماعی سنجیده می‌شوند. اهداف و ذینفعان را معرفی کنید.</p>
       </div>
       <div className="col-span-full">
          <Input 
            label="مبلغ مورد نیاز برای پروژه (تومان)"
            type="text"
            value={formData.price}
            onChange={e => handleCurrencyChange('price', e.target.value)}
            placeholder="کل بودجه مورد نیاز برای اجرای پروژه"
            dir="ltr"
          />
          <Input 
            label="نسبت امتیاز (Point Ratio)"
            type="number"
            value={formData.pointRatio}
            onChange={e => updateForm('pointRatio', e.target.value)}
            placeholder="مثلا: ۱"
          />
          <div className="col-span-full">
            <label className="block text-sm font-medium text-gray-700 mb-1.5">سطوح تقدیر</label>
            {formData.recognitionLevels.map((rl, index) => (
              <div key={index} className="flex gap-2 mb-2">
                <Input value={rl.minUnits} onChange={e => updateRecognitionLevel(index, 'minUnits', e.target.value)} placeholder="حداقل واحد" />
                <Input value={rl.label} onChange={e => updateRecognitionLevel(index, 'label', e.target.value)} placeholder="عنوان" />
                <Button onClick={() => removeRecognitionLevel(index)}><Trash2 size={16}/></Button>
              </div>
            ))}
            <Button onClick={addRecognitionLevel}><Plus size={16}/> افزودن سطح تقدیر</Button>
          </div>
       </div>
       <div className="col-span-full">
          <Input 
            label="هدف نهایی پروژه *"
            placeholder="مثلا: احداث مدرسه در مناطق محروم"
            value={formData.projectGoal}
            onChange={e => updateForm('projectGoal', e.target.value)}
          />
       </div>
       <Input 
          label="شاخص تاثیر اجتماعی *"
         placeholder="مثلا: تعداد دانش‌آموزان تحت پوشش"
         value={formData.socialImpact}
         onChange={e => updateForm('socialImpact', e.target.value)}
       />
       <Input 
         label="روش گزارش‌دهی"
         placeholder="مثلا: گزارش فصلی تصویری"
         value={formData.reportingMethod}
         onChange={e => updateForm('reportingMethod', e.target.value)}
       />
       <div className="col-span-full">
          <label className="block text-sm font-medium text-gray-700 mb-1.5">ذینفعان اصلی</label>
          <textarea 
            className="w-full border border-gray-300 rounded-xl p-3 focus:ring-teal-500 focus:border-teal-500"
            rows={3}
            placeholder="چه کسانی از این پروژه بهره‌مند می‌شوند؟"
            value={formData.beneficiaries}
            onChange={e => updateForm('beneficiaries', e.target.value)}
          ></textarea>
       </div>

       <div className="col-span-full mt-4">
          <h4 className="font-bold text-gray-700 mb-2 text-sm">شاخص‌های بلوغ و آمادگی</h4>
          <div className="bg-gray-50 p-3 rounded-xl border border-gray-200">
             <div className="flex justify-between mb-1 text-xs font-bold text-gray-500">
                <span>سطح آمادگی عملیاتی (TRL)</span>
                <span>{formData.trl}/9</span>
             </div>
             <input type="range" min="1" max="9" value={formData.trl} onChange={e => updateForm('trl', parseInt(e.target.value))} className="w-full h-1.5 bg-gray-300 rounded-lg appearance-none cursor-pointer accent-teal-600"/>
          </div>
       </div>
    </div>
  );

  // --- Main Render Switch ---

  const renderStepContent = () => {
    switch(currentStep) {
      case 1:
        return (
          <div className="space-y-6 animate-fadeIn">
            <h2 className="text-xl font-bold mb-4">چه هدفی دارید؟</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              
              <div onClick={() => updateForm('type', 'sale')}
                className={`cursor-pointer border-2 rounded-xl p-6 transition-all flex items-center gap-4 ${formData.type === 'sale' ? 'border-teal-500 bg-teal-50' : 'border-gray-200 hover:border-gray-300'}`}>
                <div className={`p-3 rounded-full shadow-sm transition-colors ${formData.type === 'sale' ? 'bg-teal-500 text-white' : 'bg-white text-teal-600'}`}><Briefcase size={24}/></div>
                <div><h3 className="font-bold text-gray-900">فروش کسب‌وکار</h3><p className="text-sm text-gray-500">مالک هستم و قصد خروج دارم</p></div>
              </div>

              <div onClick={() => updateForm('type', 'demand')}
                className={`cursor-pointer border-2 rounded-xl p-6 transition-all flex items-center gap-4 ${formData.type === 'demand' ? 'border-teal-500 bg-teal-50' : 'border-gray-200 hover:border-gray-300'}`}>
                <div className={`p-3 rounded-full shadow-sm transition-colors ${formData.type === 'demand' ? 'bg-blue-500 text-white' : 'bg-white text-blue-600'}`}><ShoppingCart size={24}/></div>
                <div><h3 className="font-bold text-gray-900">متقاضی خرید</h3><p className="text-sm text-gray-500">خریدار هستم و دنبال فرصتم</p></div>
              </div>

              <div onClick={() => updateForm('type', 'investment')}
                className={`cursor-pointer border-2 rounded-xl p-6 transition-all flex items-center gap-4 ${formData.type === 'investment' ? 'border-teal-500 bg-teal-50' : 'border-gray-200 hover:border-gray-300'}`}>
                <div className={`p-3 rounded-full shadow-sm transition-colors ${formData.type === 'investment' ? 'bg-purple-500 text-white' : 'bg-white text-purple-600'}`}><TrendingUp size={24}/></div>
                <div><h3 className="font-bold text-gray-900">جذب سرمایه</h3><p className="text-sm text-gray-500">برای توسعه نیاز به سرمایه دارم</p></div>
              </div>

              <div onClick={() => updateForm('type', 'investor')}
                className={`cursor-pointer border-2 rounded-xl p-6 transition-all flex items-center gap-4 ${formData.type === 'investor' ? 'border-teal-500 bg-teal-50' : 'border-gray-200 hover:border-gray-300'}`}>
                <div className={`p-3 rounded-full shadow-sm transition-colors ${formData.type === 'investor' ? 'bg-orange-500 text-white' : 'bg-white text-orange-600'}`}><UserPlus size={24}/></div>
                <div><h3 className="font-bold text-gray-900">سرمایه‌گذارم</h3><p className="text-sm text-gray-500">دنبال شکار فرصت‌های ناب هستم</p></div>
              </div>

              <div onClick={() => updateForm('type', 'intangible')}
                className={`cursor-pointer border-2 rounded-xl p-6 transition-all flex items-center gap-4 ${formData.type === 'intangible' ? 'border-teal-500 bg-teal-50' : 'border-gray-200 hover:border-gray-300'}`}>
                <div className={`p-3 rounded-full shadow-sm transition-colors ${formData.type === 'intangible' ? 'bg-indigo-500 text-white' : 'bg-white text-indigo-600'}`}><Award size={24}/></div>
                <div><h3 className="font-bold text-gray-900">آگهی نامشهود</h3><p className="text-sm text-gray-500">برند، نرم‌افزار، اختراع و...</p></div>
              </div>

              {enabledTypes.micro_investment && (
                <div onClick={() => updateForm('type', 'micro_investment')}
                  className={`cursor-pointer border-2 rounded-xl p-6 transition-all flex items-center gap-4 ${formData.type === 'micro_investment' ? 'border-teal-500 bg-teal-50' : 'border-gray-200 hover:border-gray-300'}`}>
                  <div className={`p-3 rounded-full shadow-sm transition-colors ${formData.type === 'micro_investment' ? 'bg-emerald-500 text-white' : 'bg-white text-emerald-600'}`}><Coins size={24}/></div>
                  <div><h3 className="font-bold text-gray-900">سرمایه‌گذاری خرد</h3><p className="text-sm text-gray-500">جذب سرمایه از عموم مردم</p></div>
                </div>
              )}

              {enabledTypes.philanthropic && (
                <div onClick={() => updateForm('type', 'philanthropic')}
                  className={`cursor-pointer border-2 rounded-xl p-6 transition-all flex items-center gap-4 ${formData.type === 'philanthropic' ? 'border-teal-500 bg-teal-50' : 'border-gray-200 hover:border-gray-300'}`}>
                  <div className={`p-3 rounded-full shadow-sm transition-colors ${formData.type === 'philanthropic' ? 'bg-rose-500 text-white' : 'bg-white text-rose-600'}`}><Heart size={24}/></div>
                  <div><h3 className="font-bold text-gray-900">عام‌المنفعه</h3><p className="text-sm text-gray-500">جذب سرمایه برای کارهای خیریه</p></div>
                </div>
              )}
            </div>
          </div>
        );
      case 2:
        return (
          <div className="space-y-6">
            <h2 className="text-xl font-bold mb-1">
              {formData.type === 'sale' && 'اطلاعات مالی و عملیاتی'}
              {formData.type === 'demand' && 'بودجه و شرایط خرید'}
              {formData.type === 'investment' && 'ساختار جذب سرمایه'}
              {formData.type === 'investor' && 'ظرفیت سرمایه‌گذاری'}
              {formData.type === 'intangible' && 'مشخصات آگهی نامشهود'}
              {formData.type === 'micro_investment' && 'جزئیات سرمایه‌گذاری خرد'}
              {formData.type === 'philanthropic' && 'جزئیات پروژه عام‌المنفعه'}
            </h2>
            <p className="text-xs text-rose-600 mb-4">فیلدهای ستاره‌دار (*) اجباری هستند.</p>
            
            {formData.type === 'sale' && renderSaleMetrics()}
            {formData.type === 'demand' && renderDemandMetrics()}
            {formData.type === 'investment' && renderInvestmentMetrics()}
            {formData.type === 'investor' && renderInvestorMetrics()}
            {formData.type === 'intangible' && renderIntangibleMetrics()}
            {formData.type === 'micro_investment' && renderMicroInvestmentMetrics()}
            {formData.type === 'philanthropic' && renderPhilanthropicMetrics()}
          </div>
        );
      case 3:
        return (
          <div className="space-y-6 animate-fadeIn">
            <h2 className="text-xl font-bold">جزئیات عمومی و معرفی</h2>
            <p className="text-xs text-rose-600">فیلدهای ستاره‌دار (*) اجباری هستند.</p>
            
            <Input 
                label="عنوان جذاب آگهی *"
                placeholder={formData.type === 'investor' ? 'مثلا: سرمایه‌گذاری روی استارتاپ‌های فین‌تک' : 'مثلا: فروش فروشگاه اینترنتی با ۵ سال سابقه'}
                value={formData.title}
                onChange={e => updateForm('title', e.target.value)}
            />

            <div className="grid grid-cols-2 gap-4">
                 <div className="col-span-1">
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">دسته‌بندی / صنعت <span className="text-rose-600">*</span></label>
                    <select 
                       className="w-full rounded-xl border-gray-300 shadow-sm focus:border-teal-500 focus:ring-teal-500 py-2.5 px-3 border"
                       value={formData.category}
                       onChange={e => updateForm('category', e.target.value)}
                    >
                       <option value="">انتخاب کنید...</option>
                       {categories.map(cat => (
                           <option key={cat.id} value={cat.id}>{cat.name}</option>
                       ))}
                    </select>
                 </div>
                 <Input 
                    label="موقعیت مکانی (استان) *"
                   placeholder="تهران"
                   value={formData.location}
                   onChange={e => updateForm('location', e.target.value)}
                 />
            </div>
            
            {formData.type === 'sale' && (
                <Input 
                  label="قیمت پیشنهادی فروش (تومان) *"
                  type="text"
                  value={formData.price}
                  onChange={e => handleCurrencyChange('price', e.target.value)}
                  dir="ltr"
                />
            )}
            
            {formData.type === 'intangible' && (
                <Input 
                  label="قیمت پیشنهادی (تومان)"
                  type="text"
                  value={formData.price}
                  onChange={e => handleCurrencyChange('price', e.target.value)}
                  dir="ltr"
                  placeholder="مثلا: ۵۰۰,۰۰۰,۰۰۰"
                />
            )}
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">توضیحات کامل (داستان کسب‌وکار) <span className="text-rose-600">*</span></label>
              <textarea 
                className="w-full border border-gray-300 rounded-xl p-3 focus:ring-teal-500 focus:border-teal-500"
                rows={5}
                placeholder="در مورد مزیت‌های رقابتی، مشتریان و دلیل واگذاری/جذب سرمایه بنویسید..."
                value={formData.description}
                onChange={e => updateForm('description', e.target.value)}
              ></textarea>
            </div>

            {/* Image Upload */}
            <div className="space-y-4">
                <label className="block text-sm font-medium text-gray-700">تصاویر آگهی (تصویر اول به عنوان تصویر اصلی نمایش داده می‌شود)</label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {formData.imageUrls.map((url, index) => (
                        <div key={index} className="relative aspect-square rounded-xl overflow-hidden border border-gray-200">
                            <img src={url} alt={`Preview ${index}`} className="w-full h-full object-cover" />
                            <button 
                                onClick={() => updateForm('imageUrls', formData.imageUrls.filter((_, i) => i !== index))}
                                className="absolute top-1 right-1 bg-red-500 text-white p-1 rounded-full"
                            >
                                <Trash2 size={14}/>
                            </button>
                        </div>
                    ))}
                    <label 
                        htmlFor="image-upload"
                        className="border-2 border-dashed border-gray-300 rounded-xl p-4 text-center cursor-pointer hover:bg-gray-50 flex flex-col items-center justify-center aspect-square"
                    >
                        <Upload className="text-gray-400 mb-2" />
                        <span className="text-xs text-gray-500">افزودن تصویر</span>
                    </label>
                    <input 
                        type="file" 
                        id="image-upload" 
                        className="hidden" 
                        accept="image/*"
                        multiple
                        onChange={handleImageUpload}
                    />
                </div>
            </div>

            {/* Video Upload */}
            <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">ویدیو آگهی (حداکثر ۲ ویدیو - اختیاری)</label>
                <input 
                    type="file" 
                    accept="video/*"
                    multiple
                    onChange={handleVideoUpload}
                    className="w-full p-2 border border-gray-300 rounded-xl"
                />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
                    {formData.videoUrls.map((url, index) => (
                        <div key={index} className="relative rounded-xl overflow-hidden bg-black aspect-video">
                            <video src={url} controls className="w-full h-full" />
                            <button 
                                onClick={() => updateForm('videoUrls', formData.videoUrls.filter((_, i) => i !== index))}
                                className="absolute top-2 right-2 bg-red-600 text-white p-1 rounded-full hover:bg-red-700 transition-colors"
                            >
                                <Trash2 size={16} />
                            </button>
                        </div>
                    ))}
                </div>
            </div>

            {/* Verification Documents */}
            <div className="border-t border-gray-200 pt-6">
                <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2"><ShieldCheck size={20}/> مدارک تاییدیه (اختیاری)</h3>
                <p className="text-sm text-gray-500 mb-4">بارگذاری این مدارک باعث افزایش اعتماد خریداران و نمایش نشان تایید روی آگهی شما می‌شود.</p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className={`p-4 rounded-xl border-2 transition-all ${formData.valuationFileUrl ? 'border-blue-500 bg-blue-50' : 'border-gray-100 bg-gray-50 hover:border-gray-200'}`}>
                        <div className="flex justify-between items-center mb-2">
                            <span className="text-sm font-bold text-gray-700">فایل ارزش‌گذاری</span>
                            {formData.valuationFileUrl && <Check size={16} className="text-blue-600" />}
                        </div>
                        <input 
                            type="file" 
                            id="valuation-file" 
                            className="hidden" 
                            accept=".pdf,image/*"
                            onChange={(e) => handleVerificationUpload(e, 'valuation')}
                        />
                        <label htmlFor="valuation-file" className="cursor-pointer text-xs text-blue-600 hover:underline flex items-center gap-1">
                            <Upload size={14} /> {docUploading === 'valuation' ? 'در حال آپلود...' : (formData.valuationFileUrl ? 'تغییر فایل' : 'بارگذاری فایل')}
                        </label>
                        {formData.valuationFileUrl && <div className="text-[11px] text-blue-500 mt-1 truncate">فایل بارگذاری شد</div>}
                    </div>

                    <div className={`p-4 rounded-xl border-2 transition-all ${formData.financialTurnoverFileUrl ? 'border-green-500 bg-green-50' : 'border-gray-100 bg-gray-50 hover:border-gray-200'}`}>
                        <div className="flex justify-between items-center mb-2">
                            <span className="text-sm font-bold text-gray-700">فایل گردش مالی</span>
                            {formData.financialTurnoverFileUrl && <Check size={16} className="text-green-600" />}
                        </div>
                        <input 
                            type="file" 
                            id="turnover-file" 
                            className="hidden" 
                            accept=".pdf,image/*"
                            onChange={(e) => handleVerificationUpload(e, 'turnover')}
                        />
                        <label htmlFor="turnover-file" className="cursor-pointer text-xs text-green-600 hover:underline flex items-center gap-1">
                            <Upload size={14} /> {docUploading === 'turnover' ? 'در حال آپلود...' : (formData.financialTurnoverFileUrl ? 'تغییر فایل' : 'بارگذاری فایل')}
                        </label>
                        {formData.financialTurnoverFileUrl && <div className="text-[11px] text-green-500 mt-1 truncate">فایل بارگذاری شد</div>}
                    </div>

                    <div className={`p-4 rounded-xl border-2 transition-all ${formData.legalVerificationFileUrl ? 'border-purple-500 bg-purple-50' : 'border-gray-100 bg-gray-50 hover:border-gray-200'}`}>
                        <div className="flex justify-between items-center mb-2">
                            <span className="text-sm font-bold text-gray-700">تاییدیه حقوقی</span>
                            {formData.legalVerificationFileUrl && <Check size={16} className="text-purple-600" />}
                        </div>
                        <input 
                            type="file" 
                            id="verification3-file" 
                            className="hidden" 
                            accept=".pdf,image/*"
                            onChange={(e) => handleVerificationUpload(e, 'legal')}
                        />
                        <label htmlFor="verification3-file" className="cursor-pointer text-xs text-purple-600 hover:underline flex items-center gap-1">
                            <Upload size={14} /> {docUploading === 'legal' ? 'در حال آپلود...' : (formData.legalVerificationFileUrl ? 'تغییر فایل' : 'بارگذاری فایل')}
                        </label>
                        {formData.legalVerificationFileUrl && <div className="text-[11px] text-purple-500 mt-1 truncate">فایل بارگذاری شد</div>}
                    </div>

                    <div className={`p-4 rounded-xl border-2 transition-all ${formData.technicalVerificationFileUrl ? 'border-amber-500 bg-amber-50' : 'border-gray-100 bg-gray-50 hover:border-gray-200'}`}>
                        <div className="flex justify-between items-center mb-2">
                            <span className="text-sm font-bold text-gray-700">تاییدیه فنی</span>
                            {formData.technicalVerificationFileUrl && <Check size={16} className="text-amber-600" />}
                        </div>
                        <input 
                            type="file" 
                            id="verification4-file" 
                            className="hidden" 
                            accept=".pdf,image/*"
                            onChange={(e) => handleVerificationUpload(e, 'technical')}
                        />
                        <label htmlFor="verification4-file" className="cursor-pointer text-xs text-amber-600 hover:underline flex items-center gap-1">
                            <Upload size={14} /> {docUploading === 'technical' ? 'در حال آپلود...' : (formData.technicalVerificationFileUrl ? 'تغییر فایل' : 'بارگذاری فایل')}
                        </label>
                        {formData.technicalVerificationFileUrl && <div className="text-[11px] text-amber-600 mt-1 truncate">فایل بارگذاری شد</div>}
                    </div>
                </div>
                {(formData.valuationFileUrl || formData.financialTurnoverFileUrl || formData.legalVerificationFileUrl || formData.technicalVerificationFileUrl) && (
                    <p className="text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-lg p-2 mt-3">
                        مدارک شما بارگذاری شد و پس از بررسی ادمین، نشان تایید روی کارت و صفحه آگهی نمایش داده می‌شود.
                    </p>
                )}
            </div>

          </div>
        );
      case 4:
        return (
          <div className="space-y-6 animate-fadeIn">
            {id ? (
              <div className="space-y-6">
                <h2 className="text-xl font-bold mb-4">ذخیره تغییرات آگهی</h2>
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-sm text-blue-800">
                  این آگهی در حال ویرایش است. برای ذخیره تغییرات نیازی به پرداخت نیست.
                </div>
                <Button onClick={handlePayment} disabled={loading} fullWidth size="lg" className="shadow-xl shadow-teal-500/30">
                  {loading ? 'در حال ذخیره...' : 'ذخیره تغییرات آگهی'}
                </Button>
              </div>
            ) : (
              <>

            <h2 className="text-xl font-bold mb-4">انتخاب نوع ثبت و پرداخت</h2>
            {isInvestorType && (
                <div className="mb-6 p-4 rounded-xl border border-green-100 bg-green-50 text-sm text-green-800">
                    برای دسته «سرمایه‌گذارم» تعرفه ثبت اصلی دریافت نمی‌شود و فقط خدمات انتخابی هزینه دارند.
                </div>
            )}
            
            {!isInvestorType && (
            <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div 
                    className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${formData.brokerageType === 'brokerage' ? 'border-teal-500 bg-teal-50' : 'border-gray-200 bg-white hover:border-gray-300'}`}
                    onClick={() => updateForm('brokerageType', 'brokerage')}
                >
                    <div className="flex justify-between items-start mb-2">
                        <div className="font-bold text-gray-900">ثبت با کارگزاری مرکز نوآوری تک</div>
                        <div className={`w-5 h-5 rounded-full border flex items-center justify-center ${formData.brokerageType === 'brokerage' ? 'border-teal-600 bg-teal-600 text-white' : 'border-gray-300'}`}>
                            {formData.brokerageType === 'brokerage' && <Check size={12}/>}
                        </div>
                    </div>
                    <p className="text-sm text-gray-600 mb-3">نمایش شماره تماس مرکز نوآوری به جای شماره شما. نیازمند تایید تعهدنامه و تعیین درصد کارگزاری.</p>
                    <div className="font-bold text-teal-700">{formatPrice(pricing.brokerageFee || pricing.baseFee)} تومان</div>
                </div>

                <div 
                    className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${formData.brokerageType === 'direct' ? 'border-teal-500 bg-teal-50' : 'border-gray-200 bg-white hover:border-gray-300'}`}
                    onClick={() => updateForm('brokerageType', 'direct')}
                >
                    <div className="flex justify-between items-start mb-2">
                        <div className="font-bold text-gray-900">ثبت مستقیم (بدون کارگزاری)</div>
                        <div className={`w-5 h-5 rounded-full border flex items-center justify-center ${formData.brokerageType === 'direct' ? 'border-teal-600 bg-teal-600 text-white' : 'border-gray-300'}`}>
                            {formData.brokerageType === 'direct' && <Check size={12}/>}
                        </div>
                    </div>
                    <p className="text-sm text-gray-600 mb-3">نمایش مستقیم شماره تماس شما به کاربران. بدون نیاز به پرداخت درصد کارگزاری.</p>
                    <div className="font-bold text-teal-700">{formatPrice(pricing.baseFee)} تومان</div>
                </div>
            </div>

            {formData.brokerageType === 'brokerage' && (
                <div className="bg-blue-50 p-4 rounded-xl border border-blue-100 mb-6">
                    <h4 className="font-bold text-blue-900 mb-2">تعهدنامه کارگزاری</h4>
                    <p className="text-sm text-blue-800 mb-4">
                        با انتخاب این گزینه، شما متعهد می‌شوید که در صورت انجام موفقیت‌آمیز معامله از طریق مرکز نوآوری تک، درصد تعیین شده زیر را به عنوان حق کارگزاری پرداخت نمایید.
                    </p>
                    <Input 
                        label="درصد پیشنهادی کارگزاری (%)"
                        type="number"
                        max={100}
                        value={formData.brokeragePercentage}
                        onChange={e => updateForm('brokeragePercentage', e.target.value)}
                        placeholder="مثلا: ۵"
                    />
                    <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                        <p className="text-xs text-amber-800 mb-3">
                            <strong>توجه:</strong> درصد شما در روند پیگیری پرونده شما تاثیر دارد.
                        </p>
                        <div className="bg-white p-3 rounded-lg border border-amber-100 mb-3 text-sm text-gray-700 max-h-40 overflow-y-auto">
                            {brokerageTermsContent || 'در حال بارگذاری شرایط کارگزاری...'}
                        </div>
                        <label className="flex items-start gap-2 cursor-pointer">
                            <input 
                                type="checkbox"
                                checked={formData.acceptedBrokerageTerms || false}
                                onChange={e => updateForm('acceptedBrokerageTerms', e.target.checked)}
                                className="mt-1 w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
                            />
                            <span className="text-sm text-gray-800">
                                شرایط کارگزاری را مطالعه کرده و می‌پذیرم
                            </span>
                        </label>
                    </div>
                </div>
            )}
            </>
            )}

            <div className="space-y-3">
                <h3 className="font-bold text-gray-800 text-sm mb-2">خدمات قابل افزودن</h3>
                
                {availableServices.map(service => {
                   const isSelected = formData.selectedServices.includes(service.id);
                   const Icon = IconMap[service.icon] || Star;
                   
                   return (
                      <div 
                        key={service.id}
                        className={`p-4 rounded-xl border cursor-pointer transition-all flex justify-between items-center ${isSelected ? 'border-teal-500 bg-teal-50' : 'border-gray-200 bg-white hover:border-gray-300'}`} 
                        onClick={() => toggleService(service.id)}
                      >
                        <div className="flex items-center gap-3">
                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${isSelected ? 'bg-teal-600 text-white' : 'bg-gray-100 text-gray-500'}`}>
                                <Icon size={18} />
                            </div>
                            <div>
                                <div className="font-bold text-sm text-gray-900">{service.title}</div>
                                <div className="text-xs text-gray-500 mt-0.5">{service.description}</div>
                            </div>
                        </div>
                         <div className="flex items-center gap-3">
                            <span className={`font-bold ${isSelected ? 'text-teal-700' : 'text-gray-700'}`}>
                                {service.price > 0 ? `+${formatPrice(service.price)}` : 'تماس بگیرید'}
                            </span>
                            <div className={`w-5 h-5 rounded-full border flex items-center justify-center ${isSelected ? 'border-teal-600 bg-teal-600 text-white' : 'border-gray-300'}`}>
                                {isSelected && <Check size={12}/>}
                            </div>
                         </div>
                      </div>
                   );
                })}
                
                {availableServices.length === 0 && (
                    <div className="text-center text-gray-400 text-sm py-4">خدمت اضافه‌ای تعریف نشده است.</div>
                )}
            </div>

            <div className="mt-8 pt-6 border-t border-gray-200">
                <h3 className="font-bold text-gray-800 text-sm mb-4">کد تخفیف</h3>
                <div className="flex gap-2">
                    <Input 
                        type="text"
                        placeholder="کد تخفیف خود را وارد کنید"
                        value={discountInput}
                        onChange={(e) => setDiscountInput(e.target.value)}
                        dir="ltr"
                        containerClassName="flex-1"
                    />
                    <Button onClick={handleApplyDiscount} variant="outline" className="h-[42px] px-6">اعمال</Button>
                </div>
                {discountError && <p className="text-red-500 text-xs mt-2">{discountError}</p>}
                {appliedDiscount && (
                    <div className="mt-2 p-3 bg-green-50 border border-green-200 rounded-xl flex justify-between items-center">
                        <span className="text-green-800 text-sm font-medium">کد تخفیف {appliedDiscount.discountPercent} درصدی اعمال شد</span>
                        <button onClick={() => { setAppliedDiscount(null); setDiscountInput(''); }} className="text-red-500 text-xs hover:underline">حذف</button>
                    </div>
                )}
            </div>

            <div className="border-t border-gray-200 pt-6 mt-6">
                <div className="flex justify-between items-center mb-6">
                    <span className="text-xl font-bold text-gray-900">مبلغ قابل پرداخت:</span>
                    <span className="text-2xl font-black text-teal-600">{formatPrice(calculateTotal())} <span className="text-sm font-normal text-gray-500">تومان</span></span>
                </div>
                <Button onClick={handlePayment} disabled={loading} fullWidth size="lg" className="shadow-xl shadow-teal-500/30">
                    {loading ? 'در حال پردازش...' : (calculateTotal() > 0 ? 'پرداخت و ثبت نهایی آگهی' : 'ثبت نهایی آگهی')}
                </Button>
            </div>
              </>
            )}
          </div>
        );
      default: return null;
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 py-8">
       {/* Progress Bar */}
       <div className="mb-8">
         <div className="flex justify-between mb-2">
           {STEPS.map((step) => (
             <div 
                key={step.id} 
                className={`flex flex-col items-center ${step.id <= currentStep ? 'text-teal-600' : 'text-gray-400'}`}
             >
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold mb-1 transition-colors ${step.id <= currentStep ? 'bg-teal-600 text-white' : 'bg-gray-200'}`}>
                  {step.id < currentStep ? <Check size={16}/> : step.id}
                </div>
                <span className="text-xs font-medium">{step.title}</span>
             </div>
           ))}
         </div>
         <div className="w-full bg-gray-200 h-2 rounded-full overflow-hidden">
            <div 
              className="bg-teal-600 h-full transition-all duration-300"
              style={{ width: `${((currentStep - 1) / (STEPS.length - 1)) * 100}%` }}
            ></div>
         </div>
       </div>

       <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 md:p-8">
          {renderStepContent()}

          <div className="flex justify-between mt-8 pt-6 border-t border-gray-100">
             <Button 
               variant="ghost" 
               onClick={handlePrev} 
               disabled={currentStep === 1}
               className={currentStep === 1 ? 'invisible' : ''}
             >
               <ChevronRight size={20} className="ml-1"/> مرحله قبل
             </Button>
             {currentStep < 4 && (
               <Button onClick={handleNext}>
                 مرحله بعد <ChevronLeft size={20} className="mr-1"/>
               </Button>
             )}
          </div>
       </div>
    </div>
  );
};

export default CreateListingPage;

